/**
 * GlobalStateEditor (WS-4b) — the editor for an app's declared **`$global`**
 * state bag: the app-global twin of `StateEditor` (`$page`). It lives in the
 * builder rail's BOTTOM (app-global) zone, opened as a main-pane AREA.
 *
 * Thin wrapper over the shared `StateScopeEditor` (state-scope unification) — it
 * adapts the index-keyed `AppGlobalStateEntry[]` onto `ScopeEntry`, injects the
 * `$global` labels + lifecycle hint + name validation, and threads the parent's
 * index-based controlled selection through the shell's key-based one.
 *
 * `$global` SURVIVES navigation within the portal session (the runtime bag is
 * the client `portalStore`); these rows are the AUTHORING declaration (name +
 * optional type + initial seed) persisted on the APP (`apps.global_state`,
 * WS-4a) — NOT on the page AST. Entries are keyed by NAME (no `id`, mirroring the
 * backend `AppGlobalStateEntry`); the parent persists the whole list via
 * `appsApi.update(appId, { global_state })`.
 */
import { Globe } from "lucide-react";

import type { AppGlobalStateEntry } from "@/lib/api-apps";

import { StateScopeEditor, type ScopeEntry } from "./StateScopeEditor";

/** A JS identifier with no leading `$` (reserved for scope roots) — mirrors the
 *  backend `_GLOBAL_STATE_NAME_RE`, so the editor flags a bad name BEFORE save. */
const NAME_RE = /^[A-Za-z_]\w*$/;

type AreaView = "list" | "detail" | "full";

export function GlobalStateEditor({
  entries,
  onChange,
  saving = false,
  view = "full",
  selectedIdx,
  onSelectIdx,
}: {
  entries: AppGlobalStateEntry[];
  onChange: (entries: AppGlobalStateEntry[]) => void;
  saving?: boolean;
  view?: AreaView;
  /** Controlled selection (master/detail split). Omit for the legacy single pane. */
  selectedIdx?: number;
  onSelectIdx?: (i: number) => void;
}) {
  const names = entries.map((e) => e.name);
  // `$global` entries have no id → the stable key is the index.
  const scopeEntries: ScopeEntry[] = entries.map((e, i) => ({
    key: String(i),
    name: e.name,
    type: e.type,
    initial: e.initial,
  }));

  const controlled = onSelectIdx !== undefined;

  const add = (): string => {
    const base = "globalVar";
    let n = base;
    let i = 1;
    while (names.includes(n)) n = `${base}${++i}`;
    onChange([...entries, { name: n, initial: null }]);
    return String(entries.length);
  };
  const patch = (key: string, next: Partial<ScopeEntry>) =>
    onChange(entries.map((e, i) => (i === Number(key) ? { ...e, ...next } : e)));
  const remove = (entry: ScopeEntry) => {
    if (window.confirm(`Eliminare lo stato globale "${entry.name}"?`)) {
      const at = Number(entry.key);
      onChange(entries.filter((_, i) => i !== at));
    }
  };

  return (
    <StateScopeEditor
      entries={scopeEntries}
      icon={Globe}
      listTitle="Variabili globali"
      addLabel="Nuova variabile globale"
      emptyListLabel="Nessuna variabile globale."
      nameAriaLabel="Nome variabile globale"
      deleteTitle="Elimina variabile globale"
      deleteLabel="Elimina variabile globale"
      emptyDetailHint={
        <>
          <p>Lo stato globale ($global) sopravvive alla navigazione tra le pagine.</p>
          <p className="font-mono text-[10px]">{`{{ $global.nome }}`} · azioni global_value / nodo JS</p>
        </>
      }
      showType
      saving={saving}
      nameError={(e) => ({
        dupe: names.indexOf(e.name) !== Number(e.key),
        bad: !NAME_RE.test(e.name),
      })}
      view={view}
      selectedKey={controlled ? String(selectedIdx ?? 0) : undefined}
      onSelectKey={controlled ? (k) => onSelectIdx!(Number(k ?? 0)) : undefined}
      onAdd={add}
      onPatch={patch}
      onRemove={remove}
    />
  );
}
